/**
 * Kinng.app — PlanCard.
 * Card compartilhado entre landing, self-signup e preview do Painel Mestre.
 * 100% dirigido por dados vindos de /api/public/plans ou form ao vivo.
 */

export const FEATURE_LABELS = {
    agenda:            { icon: "fa-calendar-days",    label: "Agenda completa" },
    pdv:               { icon: "fa-cash-register",     label: "Terminal Balcão" },
    whatsapp:          { icon: "fa-comment",           label: "WhatsApp integrado" },
    financial:         { icon: "fa-chart-line",        label: "Painel Financeiro" },
    subscription_club: { icon: "fa-medal",             label: "Clube de Assinatura" },
    waitlist:          { icon: "fa-hourglass-half",    label: "Lista de Espera" },
    ai_agent:          { icon: "fa-robot",             label: "Agente IA 24h" },
    public_booking:    { icon: "fa-mobile-screen",     label: "Booking online" },
    custom_domain:     { icon: "fa-globe",             label: "Domínio próprio" },
    priority_support:  { icon: "fa-headset",           label: "Suporte prioritário" },
    advanced_reports:  { icon: "fa-chart-pie",         label: "Relatórios avançados" },
};

const formatLimit = (v) => (v === -1 ? "Ilimitado" : v);

const formatBRL = (v) => {
    const num = Number(v || 0);
    if (!num) return "Grátis";
    return num.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

/**
 * @param {object} plan  — plano vindo do backend (ou form ao vivo)
 * @param {boolean} highlighted — destaque dourado do card do meio
 * @param {boolean} selected — estado selecionado (self-signup)
 * @param {function} onSelect — callback ao clicar
 * @param {"landing"|"signup"|"preview"} variant — pequenas variações estéticas
 * @param {string} testId — override do data-testid raiz
 */
export const PlanCard = ({
    plan,
    highlighted = false,
    selected = false,
    onSelect,
    variant = "signup",
    ctaLabel,
    testId,
}) => {
    if (!plan) return null;
    const featureList = Object.entries(plan.features || {})
        .filter(([, v]) => Boolean(v))
        .map(([k]) => ({ key: k, ...(FEATURE_LABELS[k] || { icon: "fa-check", label: k }) }));

    const priceStr = formatBRL(plan.price_monthly);
    const isFree = Number(plan.price_monthly || 0) === 0;
    const trial = Number(plan.trial_days || 0);
    const limits = plan.limits || {};

    const rootTestId = testId || `plan-card-${plan.slug || plan.id || "new"}`;
    const clickable = typeof onSelect === "function";

    return (
        <div
            data-testid={rootTestId}
            data-selected={selected ? "true" : "false"}
            data-highlighted={highlighted ? "true" : "false"}
            role={clickable ? "button" : undefined}
            tabIndex={clickable ? 0 : undefined}
            onClick={clickable ? onSelect : undefined}
            onKeyDown={clickable ? (e) => (e.key === "Enter" || e.key === " ") && onSelect() : undefined}
            className={`relative rounded-2xl border p-5 flex flex-col transition-all backdrop-blur-xl ${
                selected
                    ? "border-amber-400 bg-amber-500/10 shadow-[0_0_30px_-10px_rgba(245,158,11,0.8)]"
                    : highlighted
                        ? "border-amber-400/70 bg-black/60 shadow-[0_0_40px_-15px_rgba(212,175,55,0.6)] scale-[1.02]"
                        : "border-amber-500/15 bg-black/55 hover:border-amber-500/40"
            } ${clickable ? "cursor-pointer hover:-translate-y-0.5" : ""}`}
        >
            {highlighted && !selected && (
                <span
                    data-testid={`${rootTestId}-badge-popular`}
                    className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full text-[9px] uppercase tracking-[0.25em] font-bold text-slate-950 whitespace-nowrap shadow-lg"
                    style={{ backgroundColor: plan.color || "#c9983a" }}
                >
                    Popular
                </span>
            )}

            <header className="flex items-start gap-3 pb-4 border-b border-white/5">
                <div
                    className="h-10 w-10 rounded-xl flex items-center justify-center flex-shrink-0 border"
                    style={{
                        backgroundColor: `${plan.color || "#c9983a"}22`,
                        borderColor: `${plan.color || "#c9983a"}55`,
                        color: plan.color || "#c9983a",
                    }}
                >
                    <i className="fa-solid fa-crown"></i>
                </div>
                <div className="flex-1 min-w-0">
                    <h3
                        className="salon-heading text-lg tracking-wider text-white uppercase truncate"
                        data-testid={`${rootTestId}-name`}
                    >
                        {plan.name || "Sem nome"}
                    </h3>
                    {plan.description && (
                        <p className="text-[11px] text-slate-400 mt-0.5 line-clamp-2">{plan.description}</p>
                    )}
                </div>
                {selected && variant === "signup" && (
                    <div className="text-amber-300 shrink-0"><i className="fa-solid fa-circle-check"></i></div>
                )}
            </header>

            <div className="py-4 border-b border-white/5">
                <div className="flex items-baseline gap-1">
                    {!isFree && <span className="text-xs text-slate-400">R$</span>}
                    <span className="salon-heading text-4xl text-white leading-none" data-testid={`${rootTestId}-price`}>
                        {priceStr}
                    </span>
                    {!isFree && <span className="text-xs text-slate-500">/mês</span>}
                </div>
                {trial > 0 && (
                    <div className="text-[11px] text-emerald-300 mt-1.5">{trial} dias grátis</div>
                )}
                {plan.price_annual > 0 && !isFree && (
                    <div className="text-[10px] text-slate-500 mt-0.5">
                        ou R$ {formatBRL(plan.price_annual)}/ano
                    </div>
                )}
            </div>

            <div className="py-3 border-b border-white/5 text-[11px] text-slate-300 space-y-1">
                <div><span className="text-slate-500">Clientes:</span> <span className="font-semibold">{formatLimit(limits.clients)}</span></div>
                <div><span className="text-slate-500">Profissionais:</span> <span className="font-semibold">{formatLimit(limits.professionals)}</span></div>
                <div><span className="text-slate-500">Armazenamento:</span> <span className="font-semibold">{limits.storage_gb === -1 ? "Ilimitado" : `${limits.storage_gb} GB`}</span></div>
            </div>

            <ul className="py-4 space-y-1.5 flex-1" data-testid={`${rootTestId}-features`}>
                {featureList.length === 0 && (
                    <li className="text-[11px] text-slate-500 italic">Nenhuma feature selecionada</li>
                )}
                {featureList.map((f) => (
                    <li key={f.key} className="flex items-start gap-2 text-xs text-slate-200">
                        <i className={`fa-solid ${f.icon} mt-0.5 text-[11px]`} style={{ color: plan.color || "#c9983a" }}></i>
                        <span>{f.label}</span>
                    </li>
                ))}
            </ul>

            {clickable && (
                <button
                    type="button"
                    data-testid={`${rootTestId}-cta`}
                    onClick={(e) => { e.stopPropagation(); onSelect(); }}
                    className={`mt-2 w-full min-h-[44px] rounded-xl text-xs uppercase tracking-[0.15em] font-bold transition-all ${
                        selected || highlighted
                            ? "bg-amber-500 hover:bg-amber-400 text-slate-950 shadow-lg shadow-amber-500/30"
                            : "border border-amber-500/40 hover:border-amber-400 text-amber-200 hover:bg-amber-500/10"
                    }`}
                >
                    {ctaLabel || (selected ? "Selecionado" : `Escolher ${plan.name}`)}
                </button>
            )}
        </div>
    );
};

export default PlanCard;
